import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { OrgaoService } from '@/core/api/sicom-api/registro/orgao/orgao.service';
import { DelegaciaResumoApiResponse } from '@/core/api/sicom-api/registro/orgao/delegacia.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/**
 * Diálogo de busca de Delegacia (abrangência) — aba "Outros Filtros" do Relatório por Seleção.
 * Espelha `UntDelegacias.pas` do legado: lista as delegacias da UF informada e, ao confirmar,
 * valida que a delegacia possua ao menos um município cadastrado em sua área de abrangência
 * (regra do legado: `QryOrgaoAbrangenciaHABILITADO`, senão exibe aviso e não fecha o diálogo).
 */
@Component({
    selector: 'app-pesquisa-delegacia',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DialogoPadraoDirective, Button, Dialog, TableModule],
    templateUrl: './pesquisa-delegacia.html'
})
export class PesquisaDelegacia {
    private readonly orgaoService = inject(OrgaoService);
    private readonly messageService = inject(MessageService);
    private readonly destroyRef = inject(DestroyRef);

    /** Controla a visibilidade do diálogo; suporta two-way binding via `[(visible)]`. */
    readonly visible = model<boolean>(false);
    /** UF usada para restringir a busca (mesma UF selecionada na Aba 1 do modal de pesquisa). */
    readonly uf = input<string>('');
    /** Emite a delegacia escolhida quando o usuário confirma (já validada com abrangência). */
    readonly confirmado = output<DelegaciaResumoApiResponse>();

    readonly carregando = signal(false);
    readonly validando = signal(false);
    readonly delegacias = signal<DelegaciaResumoApiResponse[]>([]);
    readonly selecionada = signal<DelegaciaResumoApiResponse | null>(null);

    /** Carrega as delegacias da UF informada a cada exibição do diálogo. */
    onShow(): void {
        const uf = this.uf();
        this.delegacias.set([]);
        this.selecionada.set(null);
        if (!uf) return;
        this.carregando.set(true);
        this.orgaoService
            .listarDelegaciasPorUf(uf)
            .pipe(
                catchError(() => of<DelegaciaResumoApiResponse[]>([])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((lista) => {
                this.delegacias.set(lista);
                this.carregando.set(false);
            });
    }

    selecionar(d: DelegaciaResumoApiResponse): void {
        this.selecionada.set(d);
    }

    /** Só confirma se a delegacia tiver ao menos um município em sua área de abrangência. */
    confirmar(): void {
        const d = this.selecionada();
        if (!d) return;
        this.validando.set(true);
        this.orgaoService
            .listarAbrangenciasDelegacia(d.id, d.uf)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (abrangencias) => {
                    this.validando.set(false);
                    if (!abrangencias.length) {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Atenção',
                            detail: 'A delegacia selecionada não possui nenhum município cadastrado em sua área de abrangência.'
                        });
                        return;
                    }
                    this.confirmado.emit(d);
                    this.fechar();
                },
                error: () => this.validando.set(false)
            });
    }

    cancelar(): void {
        this.fechar();
    }

    private fechar(): void {
        this.visible.set(false);
        this.selecionada.set(null);
    }
}
